import { useMemo } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { TenantType, TenantFeature } from '../types/tenant.types';

/**
 * Hook for checking tenant features and type
 *
 * Usage:
 * ```tsx
 * const { hasFeature, isMaster, isAcceleratorApp } = useTenantFeatures();
 *
 * if (hasFeature(TenantFeature.INCUBATORS)) {
 *   // Render incubator content
 * }
 * ```
 */
export function useTenantFeatures() {
  const { selectedTenant } = useTenant();

  /**
   * Check if the current tenant has a specific feature enabled
   */
  const hasFeature = useMemo(() => {
    return (feature: TenantFeature): boolean => {
      if (!selectedTenant?.features) {
        return false;
      }
      return selectedTenant.features.includes(feature);
    };
  }, [selectedTenant?.features]);

  /**
   * Check if the current tenant is the Master tenant
   */
  const isMaster = useMemo(() => {
    return selectedTenant?.type === TenantType.MASTER;
  }, [selectedTenant?.type]);

  /**
   * Check if the current tenant is AcceleratorApp
   */
  const isAcceleratorApp = useMemo(() => {
    return selectedTenant?.type === TenantType.ACCELERATOR_APP;
  }, [selectedTenant?.type]);

  /**
   * Check if the current tenant is a Regular tenant
   */
  const isRegular = useMemo(() => {
    return selectedTenant?.type === TenantType.REGULAR;
  }, [selectedTenant?.type]);

  /**
   * Get the current tenant type
   */
  const tenantType = selectedTenant?.type;

  /**
   * Get all features for the current tenant
   */
  const features = selectedTenant?.features || [];

  return {
    hasFeature,
    isMaster,
    isAcceleratorApp,
    isRegular,
    tenantType,
    features,
    /** Set only on a workspace that signed up itself; null means no Revvly plan to show. */
    billingStatus: selectedTenant?.billingStatus ?? null,
  };
}

export default useTenantFeatures;
